<template>
  <div class="space-y-3 pb-2">
    <div class="space-y-1.5 text-sm">
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Customer</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">{{ customerName }}</span>
      </div>
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Order Name</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">{{
          props.order.name || '--'
        }}</span>
      </div>
      <div class="flex items-center justify-between text-grey-light-800">
        <span class="shrink-0">Order Ref</span>
        <span class="min-w-0 truncate pl-2 text-right font-semibold text-grey-light-900">
          {{ props.order.reference || '--' }}
        </span>
      </div>
    </div>

    <div class="h-[1px] w-full bg-grey-light-200" />

    <div class="flex items-center justify-between">
      <LabelComponent>Allow editing</LabelComponent>
      <SwitchComponent v-model="allowEditing" class="!mt-0" />
    </div>

    <div class="space-y-2">
      <LabelComponent>Edit Total</LabelComponent>
      <div class="relative">
        <span v-if="allowEditing" class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-500">R</span>
        <Input
          v-model="editedAmount"
          type="text"
          class="pl-7"
          :disabled="!allowEditing"
          @input="validateEditAmount"
          @blur="onEditedAmountBlur"
          placeholder="Enter new total"
        />
      </div>
      <div class="flex items-center justify-between text-grey-light-700">
        <span>Current Order Total</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(originalOrderTotal) }}</span>
      </div>
      <div
        class="flex items-center justify-between"
        :class="hasEditedAmount ? 'text-brand-700' : 'text-grey-light-700'"
      >
        <span>Total Paid</span>
        <span class="font-semibold" :class="hasEditedAmount ? 'text-brand-700' : 'text-grey-light-900'">
          {{ displayCurrency(totalPaid) }}
        </span>
      </div>
    </div>

    <div class="space-y-2" v-if="props.useTravelDate">
      <LabelComponent>Edit Travel Date</LabelComponent>
      <Popover v-model:open="showTravelDatePicker">
        <PopoverTrigger as-child>
          <Button
            class="w-full justify-start text-left font-normal"
            :class="cn(!selectedTravelDate && 'text-muted-foreground')"
            variant="outline"
            :disabled="!allowEditing"
          >
            <CalendarIcon class="mr-2 h-4 w-4" />
            {{ selectedTravelDate ? travelDateFormatted : 'Select date' }}
          </Button>
        </PopoverTrigger>

        <PopoverContent class="w-auto">
          <Calendar
            v-model="selectedTravelDate"
            :minValue="minDate"
            :weekday-format="'short'"
            class="rounded-md"
            @update:modelValue="onTravelDateChange"
          />
        </PopoverContent>
      </Popover>
      <div
        class="flex items-center justify-between"
        :class="hasSelectedTravelDate ? 'text-brand-700' : 'text-grey-light-700'"
      >
        <span>{{ hasSelectedTravelDate ? 'New Travel Date' : 'Current Travel Date' }}</span>
        <div class="flex items-center justify-end gap-1">
          <span
            v-if="hasSelectedTravelDate && travelDateOffsetLabel"
            class="inline-flex h-[22px] min-w-[84px] items-center justify-center whitespace-nowrap rounded-full border border-brand-200 bg-brand-50 px-3 py-0.5 text-sm font-semibold leading-none text-brand-700"
          >
            {{ travelDateOffsetLabel }}
          </span>
          <span class="font-semibold" :class="hasSelectedTravelDate ? 'text-brand-700' : 'text-grey-light-900'">
            {{
              hasSelectedTravelDate && selectedTravelDate
                ? displayDate(new Date(selectedTravelDate.toString()))
                : props.travelDate
                  ? displayDate(props.travelDate)
                  : 'Select date'
            }}
          </span>
        </div>
      </div>
      <div class="flex items-center justify-between text-grey-light-700">
        <span>Current Final Payment Date</span>
        <span class="font-semibold text-grey-light-900">{{ displayDate(props.endDate) }}</span>
      </div>
    </div>
    <div
      v-if="hasEditedAmount || hasSelectedTravelDate"
      class="flex flex-col gap-2 border border-grey-light-200 rounded-lg bg-grey-light-50 p-3"
    >
      <span class="text-grey-light-700 font-medium">Payment plan breakdown</span>
      <span v-if="!loadingPreview" class="text-grey-light-900 font-semibold">{{ paymentBreakdownText }}</span>
      <div v-else class="flex justify-center">
        <LayupSpinner />
      </div>
    </div>
    <Button
      class="w-full"
      :disabled="!allowEditing || (!hasEditedAmount && !hasSelectedTravelDate) || loadingPreview"
      @click="onPreviewPlan"
    >
      <LayupSpinner v-if="loadingPreview" />
      <span v-else>Preview new plan</span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import { cn } from '@/lib/utils';
import { previewAmendment } from '@/services/amendment';
import { generatePaymentPlanPreview } from '@/services/paymentPlans';
import { useMerchantStore } from '@/stores/merchants';
import { isNumber } from '@/utils/form.utils';
import { displayCurrency, displayDate, toCents } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { parseDate, type DateValue } from '@internationalized/date';
import { Calendar as CalendarIcon } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import type {
  ModelOrderAmendmentPreviewResponse,
  ModelOrderResponse,
  ModelPaymentPlanPreviewRequest,
  ModelPaymentPlanPreviewResponseMany,
} from '@layup/api-sdk';

const props = defineProps<{
  order: Partial<ModelOrderResponse>;
  endDate: string;
  travelDate: string;
  useTravelDate: boolean;
}>();
const merchantStore = useMerchantStore();

const emit = defineEmits<{
  (e: 'update:can-create-plan', value: boolean): void;
  (e: 'update:error', value: string): void;
  (e: 'preview-created', value: ModelOrderAmendmentPreviewResponse): void;
}>();

const previewText = ref('');
const loadingPreview = ref(false);
const allowEditing = ref(false);
const editedAmount = ref('');
const editedTravelDate = ref('');
const originalOrderTotal = ref(0);
const showTravelDatePicker = ref(false);
const hasSelectedTravelDate = ref(false);
const customerName = computed(() => props.order.plans?.[0]?.benefactor?.name || '--');
const totalPaid = computed(() => {
  const plans = props.order.plans ?? [];
  return plans.reduce((sum, plan: any) => {
    const paidTotal = (plan.payments ?? []).reduce((paymentSum: number, payment: any) => {
      return paymentSum + (payment.paid ? payment.amount || 0 : 0);
    }, 0);
    return sum + paidTotal;
  }, 0);
});
const hasEditedAmount = computed(() => Number(editedAmount.value) > 0);
const canPreview = computed(() => hasEditedAmount.value);

const toIsoDateString = (value?: string) => {
  if (!value) return '';

  // Already in ISO date format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // South African date format (DD/MM/YYYY)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/');
    return `${year}-${month}-${day}`;
  }

  // Fallback for ISO datetime strings and other JS-parseable dates
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const getInitialTravelDate = (): DateValue | undefined => {
  const isoDate = toIsoDateString(props.travelDate);
  return isoDate ? parseDate(isoDate) : undefined;
};

const selectedTravelDate = ref(getInitialTravelDate()) as Ref<DateValue | undefined>;

const minDate = computed(() => {
  const isoEndDate = toIsoDateString(props.endDate);
  if (!isoEndDate) return undefined;

  const minTravelDays = merchantStore.merchant.features?.numberOfDayAfterEndDateToTravel || 0;
  const endDate = new Date(`${isoEndDate}T00:00:00.000Z`);
  endDate.setUTCDate(endDate.getUTCDate() + minTravelDays);

  const minTravelDate = endDate.toISOString().split('T')[0];
  return parseDate(minTravelDate);
});

const travelDateFormatted = computed(() =>
  selectedTravelDate.value ? displayDate(new Date(selectedTravelDate.value.toString())) : '',
);

const travelDateOffsetLabel = computed(() => {
  if (!hasSelectedTravelDate.value || !selectedTravelDate.value) return '';
  const minTravelDays = merchantStore.merchant.features?.numberOfDayAfterEndDateToTravel || 0;
  return `+${minTravelDays} days`;
});

const currentOrderBreakdownText = computed(() => {
  const plan = props.order.plans?.[0];
  if (!plan) return '';

  const payments = plan.payments ?? [];
  if (!payments.length) return '';

  const unpaidPayments = payments.filter((payment) => !payment.paid);
  const referencePayments = unpaidPayments.length ? unpaidPayments : payments;
  const lastReferencePayment = referencePayments[referencePayments.length - 1];

  const amount = lastReferencePayment?.amount || 0;
  const months = referencePayments.length;

  return `${displayCurrency(amount)}/pm for ${months} months`;
});

const paymentBreakdownText = computed(() => {
  if (hasEditedAmount.value) return previewText.value;
  return currentOrderBreakdownText.value;
});

const validateAndEmitCanCreatePlan = () => {
  emit('update:can-create-plan', allowEditing.value && canPreview.value);
};

const fetchPaymentPlanPreview = async () => {
  try {
    loadingPreview.value = true;
    const amountDue = toCents(Number(editedAmount.value));

    const orderCopy: ModelPaymentPlanPreviewRequest = {
      amountDue,
      depositPerc: props.order.depositPerc,
      depositAmount: props.order.depositAmount,
      depositType: props.order.depositType,
      endDateMax: props.endDate,
      endDateMin: props.order.endDateMin,
      percFee: props.order.percFee,
      flatFee: props.order.flatFee,
    };

    if (orderCopy.depositType !== 'FLAT') {
      delete orderCopy.depositAmount;
    }

    const preview = await generatePaymentPlanPreview(orderCopy);
    buildPaymentBreakdown(preview);
  } catch (error: any) {
    if (error.response?.data) {
      const serverMessage = error.response?.data || 'Unknown error';
      emit('update:error', serverMessage as string);
    }

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create-payment-plan-preview', {
        status: 'network_error',
        channel: 'merchant',
        method: 'generatePaymentPlanPreview',
      });
    } else {
      createException(error);
      addMetric('create-payment-plan-preview', {
        status: 'error',
        channel: 'merchant',
        method: 'generatePaymentPlanPreview',
      });
    }
  } finally {
    loadingPreview.value = false;
    validateAndEmitCanCreatePlan();
  }
};

const onPreviewPlan = async () => {
  if (!allowEditing.value) return;

  const editedAmountCents = hasEditedAmount.value ? toCents(Number(editedAmount.value)) : undefined;
  const amendedProducts = Array.isArray(props.order.products)
    ? props.order.products.map((product) => ({ ...product }))
    : [];

  if (editedAmountCents != null && amendedProducts.length === 1) {
    amendedProducts[0].amount = editedAmountCents - (totalPaid.value || 0);
  }

  if (props.order._id) {
    const amendmentPayload: {
      orderId: string;
      amendmentType: 'MULTIPLE:UPDATE';
      endDate: string;
      products: typeof props.order.products;
      travelDate?: string;
    } = {
      orderId: props.order._id,
      amendmentType: 'MULTIPLE:UPDATE',
      endDate: props.endDate,
      products: amendedProducts,
    };

    if (props.useTravelDate && hasSelectedTravelDate.value && editedTravelDate.value) {
      amendmentPayload.travelDate = editedTravelDate.value;
    }

    const preview = await previewAmendment(amendmentPayload);
    emit('preview-created', preview as ModelOrderAmendmentPreviewResponse);
  }
};

onMounted(async () => {
  if (
    merchantStore.activeMerchantId &&
    typeof merchantStore.merchant?.features?.numberOfDayAfterEndDateToTravel !== 'number'
  ) {
    await merchantStore.onChangeMerchant(merchantStore.activeMerchantId);
  }
  originalOrderTotal.value = Number(props.order.amountDue || 0);
});

onUnmounted(() => emit('update:error', ''));

watch([allowEditing, editedAmount, editedTravelDate], () => validateAndEmitCanCreatePlan(), { immediate: true });

watch(
  selectedTravelDate,
  (newVal) => {
    if (newVal) {
      editedTravelDate.value = newVal.toString();
    } else {
      editedTravelDate.value = '';
    }
    showTravelDatePicker.value = false;
  },
  { immediate: true },
);

const onTravelDateChange = () => {
  if (selectedTravelDate.value) {
    editedTravelDate.value = selectedTravelDate.value.toString();
    hasSelectedTravelDate.value = true;
  }
};

const buildPaymentBreakdown = (preview: ModelPaymentPlanPreviewResponseMany) => {
  if (preview.paymentPlans) {
    // Payment plans array is a collection of options for the order at each month (1month, 2mo....)
    // Thats why the last one is picked because its the full plan
    const plan = preview.paymentPlans[preview.paymentPlans.length - 1];
    const amount = plan.payments ? plan.payments[plan.payments.length - 1].amount || 0 : 0;
    const months = plan.months;
    previewText.value = `${displayCurrency(amount)}/pm for ${months} months`;
  }
};

const validateEditAmount = (event: Event) => {
  const inputValue = isNumber(event);
  editedAmount.value = inputValue;
};

const onEditedAmountBlur = async () => {
  if (!allowEditing.value || !hasEditedAmount.value) return;
  await fetchPaymentPlanPreview();
};
</script>
